import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BATCH_ID = 'OPT-DEMO-001';

const ResolveWarningDialog = ({ open, onOpenChange, warning, user, onResolved }) => {
  const [operator, setOperator] = useState('');
  const [reason, setReason] = useState('');
  const [affectedStep, setAffectedStep] = useState('');
  const [temperature, setTemperature] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && warning) {
      setOperator(user?.name || '');
      setReason('');
      setAffectedStep(warning.expected_event || 'Quality check');
      setTemperature('');
    }
  }, [open, warning, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!operator.trim() || reason.trim().length < 3 || !affectedStep.trim()) {
      toast.error('Please fill in operator, reason, and affected step.');
      return;
    }
    // Validate temperature if provided.
    let temperatureCelsius = null;
    if (temperature.trim() !== '') {
      const parsed = Number(temperature);
      if (Number.isNaN(parsed) || parsed < -50 || parsed > 80) {
        toast.error('Temperature must be a number between -50 °C and 80 °C.');
        return;
      }
      temperatureCelsius = parsed;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/optimar/batches/${BATCH_ID}/warnings/${warning.id}/resolve`,
        {
          operator: operator.trim(),
          reason: reason.trim(),
          affected_step: affectedStep.trim(),
          temperature_celsius: temperatureCelsius,
        }
      );
      toast.success(
        `Correction ${res.data.correction_id} logged. Confidence ${res.data.new_confidence}%.`
      );
      onOpenChange(false);
      if (onResolved) onResolved(res.data);
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail || 'Failed to log correction.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="optimar-resolve-dialog">
        <DialogHeader>
          <DialogTitle>Log manual correction</DialogTitle>
          <DialogDescription>
            Record a manual correction for{' '}
            <span className="font-medium">
              {warning?.title || 'this traceability warning'}
            </span>
            . The warning will be marked resolved and kept in the audit history.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="operator">Operator</Label>
            <Input
              id="operator"
              data-testid="optimar-resolve-operator"
              value={operator}
              onChange={(e) => setOperator(e.target.value)}
              placeholder="Your name"
              autoComplete="off"
            />
          </div>
          <div>
            <Label htmlFor="affected-step">Affected production step</Label>
            <Input
              id="affected-step"
              data-testid="optimar-resolve-step"
              value={affectedStep}
              onChange={(e) => setAffectedStep(e.target.value)}
              placeholder="e.g. Quality check"
              autoComplete="off"
            />
            <p className="mt-1 text-xs text-slate-500">
              Suggested from the warning: <span className="font-medium">{warning?.expected_event}</span>
            </p>
          </div>
          <div>
            <Label htmlFor="qc-temperature">
              Temperature at QC (°C){' '}
              <span className="text-xs font-normal text-slate-400">(optional)</span>
            </Label>
            <Input
              id="qc-temperature"
              data-testid="optimar-resolve-temperature"
              type="number"
              step="0.1"
              min="-50"
              max="80"
              value={temperature}
              onChange={(e) => setTemperature(e.target.value)}
              placeholder="e.g. 2.1"
              autoComplete="off"
              inputMode="decimal"
            />
            <p className="mt-1 text-xs text-slate-500">
              Recorded reading at the QC station. Auto-populates Step 07 and the production
              history once the correction is logged.
            </p>
          </div>
          <div>
            <Label htmlFor="reason">Correction reason / action taken</Label>
            <Textarea
              id="reason"
              data-testid="optimar-resolve-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Paper QC sheet attached; reading 2.1 °C at 14:32; operator on shift verified."
              rows={4}
            />
            <p className="mt-1 text-xs text-slate-500">
              This will be persisted to the manual correction log and visible to auditors.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="optimar-resolve-submit"
              disabled={submitting}
            >
              {submitting ? 'Logging…' : 'Log correction & resolve'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ResolveWarningDialog;
